/**
 * Vitrine — Landing page (Nikki Beach inspired).
 *
 * Editorial layout: full-bleed hero, locations grid, narrative sections,
 * lifestyle gallery, story, footer. Serif headings (Cormorant), white space,
 * photos do the talking.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, Calendar } from "lucide-react";
import { trackEvent } from "../../lib/tracking";
import { useSiteConfig, sel } from "../../lib/site-config";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UNIVERS = [
  {
    to: "/univers/beach-club",
    bookOfferId: "pass_day",
    name: "Beach Club",
    description:
      "Day Pass, The Sunset, B Brunch — trois rituels signature pour vivre l'île à votre rythme. Une parenthèse exclusive entre lagune et océan, ouverte sept jours sur sept.",
    image:
      "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/trz2j0jd_BEACH%20CLUB.png",
  },
  {
    to: "/univers/hebergement",
    bookOfferId: "hebergement",
    name: "Hébergement",
    description:
      "Une nuit en suspens entre lagune et océan, dans nos suites signature. Chambres Supérieures et Suites côté jardin ou côté lagune, soins Spa & Wellness signature au bord de l'eau.",
    image:
      "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/7bcipz8w_HEBERGEMENT.png",
  },
  {
    to: "/le-kaai",
    bookOfferId: "le_kaai",
    name: "Restaurant Le Kaai",
    description:
      "Le KAAÏ est le nouveau restaurant du BBr. Une table à l'ambition gastronomique affirmée, portée par des saveurs d'inspiration africaine contemporaine, dans une atmosphère élégante et chaleureuse.",
    image:
      "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/v2f73qqm_KAAI.png",
  },
  {
    to: "/univers/corporate",
    bookOfferId: "seminaire",
    name: "Corporate",
    description:
      "Séminaires résidentiels, journées d'étude, team building, déjeuners et dîners d'entreprise — salles équipées, vue océan, hébergement et pauses gastronomiques pour vos événements professionnels.",
    image:
      "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/oy7zzngs_SEMINAIRE.png",
  },
  {
    to: "/univers/activites",
    bookOfferId: "offres_loisirs",
    name: "Activités & Events",
    description:
      "Jet ski, paddle, kayak et plus — une journée d'activités lagunaires. Privatisations, soirées privées et expériences sur-mesure pour fédérer vos équipes ou célébrer vos grands moments.",
    image:
      "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/ocqva33h_ACTIVITE.png",
  },
];

// iter-51 — Experience-First refonte: 4 emotional gateways exposed under the Hero.
// iter-57 — Each experience now opens a popup listing its sub-experiences
// instead of navigating directly to a single page.
const EXPERIENCES = [
  {
    key: "intensite",
    name: "L'Intensité",
    description: "Vivez toute l'énergie de BBr entre beach club et événements exclusifs.",
    image: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/trz2j0jd_BEACH%20CLUB.png",
    options: [
      { label: "Beach Club", to: "/univers/beach-club" },
      { label: "Loisirs", to: "/univers/activites" },
      { label: "Événements", to: "/univers/evenementiel" },
    ],
  },
  {
    key: "immersion",
    name: "L'Immersion",
    description: "Déconnectez du quotidien et profitez d'un moment de détente.",
    image: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/ocqva33h_ACTIVITE.png",
    options: [
      { label: "Lounge", to: "/univers/lounge" },
    ],
  },
  {
    key: "connexion",
    name: "La Connexion",
    description: "Réunissez-vous autour d'un repas ou d'un événement professionnel.",
    image: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/oy7zzngs_SEMINAIRE.png",
    options: [
      { label: "Restaurant Le Kaai", to: "/le-kaai" },
      { label: "Salles", to: "/univers/salles" },
    ],
  },
  {
    key: "present",
    name: "Le Présent",
    description: "Offrez-vous un séjour et emportez un souvenir de l'expérience BBr.",
    image: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/7bcipz8w_HEBERGEMENT.png",
    options: [
      { label: "Hébergement", to: "/univers/hebergement" },
    ],
  },
];

// Instagram-style posts (curated) — using existing BBR assets.
const INSTAGRAM_POSTS = [
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/4kr4z5g1_DAY%20PASS.jpeg", caption: "Day Pass" },
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/3g3onmkg_THE%20SUNSET.jpeg", caption: "The Sunset" },
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/1txrnqdp_B%20BRUNCH.jpeg", caption: "B Brunch" },
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/kgqk46mw_LE%20KAAI.jpeg", caption: "Le Kaai" },
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/ivhtbefz_BBR%20_SHOOT%202_15.jpg", caption: "Île Boulay" },
  { src: "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/2hilix5p_BBR%20_SHOOT%202_29.jpg", caption: "BBR Life" },
];

const LIFESTYLE = [
  "https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1551782450-a2132b4ba21d?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1400&q=85",
  "https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=1400&q=85",
];

// iter-87 · Fallback cover per Beach Life offer id, aligned with the
// same brand imagery used on /experience/{id} and Beach Club calendar.
const FALLBACK_OFFER_IMAGE = {
  pass_day:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/z00iovry_Image%20site%20beach%20club-02.webp",
  sunset:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
  // iter-96 · Sunset Club — reuse the sunset artwork until CMS uploads dedicated art.
  sunset_club:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/r3dvpdje_Image%20site%20beach%20club-03.webp",
  brunch:
    "https://customer-assets-jt897jd0.emergentagent.net/job_reserve-bbr/artifacts/c04z1595_Image%20site%20beach%20club_Plan%20de%20travail%201.webp",
  offres_loisirs:
    "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/66jfvevy_OFFRE%20LOISIRS.png",
};

const fmtOfferDateFR = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return iso;
  }
};

const fmtOfferPriceFR = (v) =>
  typeof v === "number" && v > 0
    ? `${new Intl.NumberFormat("fr-FR").format(v)} FCFA`
    : "";

export default function VitrineLanding() {
  const [blogArticles, setBlogArticles] = useState([]);
  // iter-87 · Dynamic "Offres de la semaine" section — Dashboard-driven.
  const [offers, setOffers] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  const cfg = useSiteConfig();
  const hero = sel.hero(cfg);
  const universCfg = sel.univers(cfg);
  const testimonialsCfg = sel.testimonials(cfg);
  const faqCfg = sel.faq(cfg);

  // Merge CMS univers list with the hardcoded routing map (bookOfferId).
  const universList = (universCfg.items && universCfg.items.length > 0
    ? universCfg.items.map((cmsItem) => {
        const fallback = UNIVERS.find((u) => u.to === cmsItem.to) || {};
        return {
          to: cmsItem.to || fallback.to,
          bookOfferId: fallback.bookOfferId,
          name: cmsItem.name || fallback.name,
          description: cmsItem.description || fallback.description,
          image: cmsItem.image || fallback.image,
        };
      })
    : UNIVERS);

  useEffect(() => {
    fetch(`${BACKEND}/api/blog/articles?limit=3`)
      .then((r) => r.json())
      .then((d) => setBlogArticles(d.items || []))
      .catch(() => {});
    // iter-87 · Fetch active offers + upcoming events for the
    // "Offres de la semaine" section (dynamic, Dashboard-driven).
    fetch(`${BACKEND}/api/offers`)
      .then((r) => r.json())
      .then((d) => setOffers(Array.isArray(d) ? d : d.items || []))
      .catch(() => {});
    fetch(`${BACKEND}/api/special-events`)
      .then((r) => r.json())
      .then((d) => setSpecialEvents(d.items || []))
      .catch(() => {});
  }, []);

  /**
   * iter-87 · Compute the next valid ISO date for a Beach Life offer
   * whose availability depends on weekday. Client selects a date first
   * on Beach Club, but on the homepage card we pre-fill the closest
   * upcoming matching day so the visitor skips straight to /experience/.
   */
  const nextValidDateForOffer = (offerId) => {
    const now = new Date();
    // Reset to local start-of-day.
    now.setHours(0, 0, 0, 0);
    // Weekday routing: 0=Sun, 1..5=Mon-Fri, 6=Sat.
    const matches = (day) => {
      if (offerId === "sunset") return day === 6;
      if (offerId === "sunset_club") return day === 6;
      if (offerId === "brunch") return day === 0;
      if (offerId === "pass_day") return day >= 1 && day <= 5;
      return true; // offres_loisirs & catch-all: any day
    };
    for (let i = 0; i < 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      if (matches(d.getDay())) {
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return iso;
      }
    }
    return null;
  };

  /**
   * iter-87 · Assemble the "Offres de la semaine" list = active
   * Beach Life offers + upcoming published special events. Fully dynamic
   * — nothing is hardcoded, everything comes from /api/offers and
   * /api/special-events (Dashboard-driven).
   */
  const weekOffers = useMemo(() => {
    // Beach Life offers: only Beach Club pole & offres_loisirs. Skip
    // technical offers (hébergement, séminaire, etc.) — this section is
    // a commercial showcase of the CURRENT week's experiences.
    const featured = offers.filter(
      (o) => ["pass_day", "sunset", "sunset_club", "brunch", "offres_loisirs"].includes(o.id),
    );
    const bl = featured.map((o) => ({
      key: `offer-${o.id}`,
      kind: "offer",
      title: o.name_fr || o.name_en || o.id,
      subtitle: o.schedule_fr || o.schedule_en || "",
      image: o.image_url || FALLBACK_OFFER_IMAGE[o.id] || "",
      price: typeof o.price_adult === "number" && o.price_adult > 0 ? o.price_adult : null,
      to: (() => {
        if (o.id === "offres_loisirs") return `/experience/${o.id}?date=${nextValidDateForOffer(o.id)}`;
        const iso = nextValidDateForOffer(o.id);
        return iso ? `/experience/${o.id}?date=${iso}` : `/univers/beach-club`;
      })(),
    }));
    // Upcoming published events sorted by earliest date.
    const todayIso = new Date().toISOString().slice(0, 10);
    const ev = specialEvents
      .filter((e) => (e.status || "published") === "published")
      .map((e) => {
        const dates = Array.isArray(e.event_dates) ? e.event_dates : [];
        const upcoming = dates.filter((d) => d >= todayIso).sort();
        const nextDate = upcoming[0] || e.start_date || null;
        return { ...e, nextDate };
      })
      .filter((e) => !!e.nextDate)
      .sort((a, b) => (a.nextDate || "").localeCompare(b.nextDate || ""))
      .map((e) => ({
        key: `event-${e.id}`,
        kind: "event",
        title: e.title || e.title_fr || "Événement",
        subtitle: e.subtitle || "",
        image: e.image_url || "",
        price: typeof e.price_adult === "number" && e.price_adult > 0 ? e.price_adult : null,
        date: e.nextDate,
        to: `/event/${e.id}`,
      }));
    return [...bl, ...ev];
  }, [offers, specialEvents]);

  return (
    <div data-testid="vitrine-landing" className="bg-white text-[#0A0A0A]">
      {/* ─── HERO ───────────────────────────────────────── */}
      <section className="relative w-full h-screen min-h-[640px] overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
          poster={hero.poster_url || "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/0frg347a_BBR%20_SHOOT%202_139.jpg.jpeg"}
        >
          <source
            src={hero.video_url || "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/4d9005uu_IMG_4425.MOV"}
            type="video/quicktime"
          />
          <source
            src={hero.video_url || "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/4d9005uu_IMG_4425.MOV"}
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <div className="text-[0.7rem] tracking-[0.55em] uppercase text-white/80 mb-10">
            {hero.kicker || "Île Boulay  ·  Abidjan"}
          </div>
          <h1
            className="font-serif text-white leading-[0.95] text-7xl sm:text-[5.5rem] md:text-[7.5rem] lg:text-[9rem] xl:text-[10.5rem] max-w-6xl"
            style={{ fontWeight: 400, letterSpacing: "0.04em" }}
            data-testid="vitrine-hero-title"
          >
            {hero.title || "LIFE IS HERE"}
          </h1>
          <p
            className="mt-10 text-xl sm:text-2xl md:text-3xl text-white/90 max-w-3xl leading-relaxed font-light"
            data-testid="vitrine-hero-subtitle"
          >
            {hero.subtitle || "Une île privée, à quelques minutes d'Abidjan. Un autre rythme. Une autre énergie. Des expériences premium inoubliables."}
          </p>
          <div className="mt-12 w-px h-12 bg-white/60" />
        </div>
      </section>

      {/* ─── CHOISISSEZ VOTRE EXPÉRIENCE — horizontal carousel with drag ─── */}
      <section id="experiences" className="py-24 md:py-32 bg-white overflow-hidden" data-testid="experiences-section">
        <div className="max-w-[1500px] mx-auto px-6 md:pl-10 md:pr-10 lg:pl-14 lg:pr-14 mb-10 md:mb-16 text-center">
          <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
            · Life is Here ·
          </div>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-5xl lg:text-6xl leading-[1.1] text-[#0A0A0A] max-w-3xl mx-auto">
            Choisissez votre expérience
          </h2>
          <p className="mt-6 md:mt-8 max-w-xl mx-auto text-base md:text-lg text-[#0A0A0A]/70 font-light leading-relaxed">
            Chaque instant à BBr est une expérience unique. Choisissez l'univers
            qui correspond à votre envie du moment et laissez-vous guider.
          </p>
        </div>

        <ExperiencesCarousel experiences={EXPERIENCES} />
      </section>

      {/* ─── BEACH CLUB — Immersive full-bleed hero ─── */}
      <section
        className="relative w-full h-[88vh] md:h-[92vh] min-h-[620px] overflow-hidden bg-[#0A0A0A]"
        data-testid="editorial-beach-club-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center scale-[1.02] transition-transform duration-[6s] ease-out"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/trz2j0jd_BEACH%20CLUB.png)",
          }}
        />
        {/* Subtle vignette for legibility around the glass card */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/25 via-black/10 to-black/45" />

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 flex items-center">
          {/* Glass content card — bottom-left on mobile, left on desktop */}
          <div className="w-full md:max-w-lg lg:max-w-xl ml-0 md:ml-4 lg:ml-8 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] p-8 sm:p-10 md:p-12 lg:p-14 text-[#0A0A0A]">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
              · Notre Beach Club ·
            </div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] mb-8 text-[#0A0A0A]">
              L'art de vivre balnéaire,<br />à l'ivoirienne.
            </h2>
            <p className="text-sm md:text-base leading-[1.85] text-[#0A0A0A]/75 font-light mb-10">
              Pionnier du concept de beach club de luxe en Afrique de l'Ouest, BBR transforme
              chaque visite en un voyage sensoriel. Service chaleureux, cuisine généreuse,
              musique programmée, vue lagune. Une parenthèse inoubliable.
            </p>
            <Link
              to="/univers/beach-club"
              onClick={() => trackEvent("view_offer", { offer: "Beach Club" })}
              className="inline-flex items-center gap-3 text-[0.68rem] md:text-[0.72rem] tracking-[0.4em] uppercase text-[#0A0A0A] pb-1.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-px after:w-8 after:bg-[#0A0A0A]/70 after:transition-all after:duration-500 hover:after:w-full hover:after:bg-[#B8922A] hover:text-[#B8922A]"
              data-testid="editorial-beach-club-cta"
            >
              Explorer le Beach Club
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── LE KAAI — Immersive full-bleed hero (CMS-driven iter-63/64) ─── */}
      {(() => {
        const kt = sel.pageHeader(cfg, "home_kaai_teaser");
        const bg = kt.image_url || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=2400&q=85";
        const video = kt.video_url || "";
        const kicker = kt.kicker || "· Le Kaai · Restaurant signature ·";
        const title = kt.title || "Une cuisine qui voyage,\nface à la lagune.";
        const body = kt.subtitle_long || "Plateaux à partager, bateaux à sushi, salades fraîches et plats internationaux inspirés des destinations qui nous façonnent. De la ferme à la table, des produits locaux d'exception, une cave construite avec passion.";
        const ctaLabel = kt.cta_label || "Découvrir Le Kaai";
        const ctaHref = kt.cta_href || "/le-kaai";
        return (
      <section
        className="relative w-full h-[88vh] md:h-[92vh] min-h-[620px] overflow-hidden bg-[#0A0A0A]"
        data-testid="editorial-kaai-section"
      >
        {video ? (
          <video
            className="absolute inset-0 w-full h-full object-cover scale-[1.02] transition-transform duration-[6s] ease-out"
            src={video}
            poster={bg}
            autoPlay muted loop playsInline preload="metadata"
            data-testid="editorial-kaai-video"
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center scale-[1.02] transition-transform duration-[6s] ease-out"
            style={{ backgroundImage: `url(${bg})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-bl from-black/25 via-black/15 to-black/55" />

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-end">
          <div className="w-full md:max-w-lg lg:max-w-xl mr-0 md:mr-4 lg:mr-8 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] p-8 sm:p-10 md:p-12 lg:p-14 text-[#0A0A0A]">
            <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
              {kicker}
            </div>
            <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] mb-8 text-[#0A0A0A] whitespace-pre-line">
              {title}
            </h2>
            <p className="text-sm md:text-base leading-[1.85] text-[#0A0A0A]/75 font-light mb-10">
              {body}
            </p>
            <Link
              to={ctaHref}
              onClick={() => trackEvent("view_offer", { offer: "Le Kaai" })}
              className="inline-flex items-center gap-3 text-[0.68rem] md:text-[0.72rem] tracking-[0.4em] uppercase text-[#0A0A0A] pb-1.5 relative after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-px after:w-8 after:bg-[#0A0A0A]/70 after:transition-all after:duration-500 hover:after:w-full hover:after:bg-[#B8922A] hover:text-[#B8922A]"
              data-testid="editorial-kaai-cta"
            >
              {ctaLabel}
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
        );
      })()}

      {/* ─── (Galerie lifestyle, Notre Histoire et Témoignages retirés — iter-51) ─── */}

      {/* ─── MOT DU FONDATEUR — Editorial two-column (iter-58, CMS-driven iter-62) ─── */}
      {(() => {
        const fw = sel.founderWord(cfg);
        if (fw.enabled === false) return null;
        const paragraphs = Array.isArray(fw.paragraphs) && fw.paragraphs.length > 0 ? fw.paragraphs : [];
        return (
      <section
        className="py-24 md:py-32 lg:py-40 bg-white"
        data-testid="founder-word-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 lg:gap-20 items-center">
            {/* Portrait — left column (first on mobile via source order) */}
            <div className="lg:col-span-5 order-1">
              <div
                className="relative w-full aspect-[4/5] bg-[#FAF7F2] overflow-hidden"
                data-testid="founder-word-image"
              >
                <img
                  src={fw.image_url || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1200&q=85"}
                  alt="Fondateur — Boulay Beach Resort"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Text — right column (second on mobile) */}
            <div className="lg:col-span-7 lg:pl-6 order-2" data-testid="founder-word-body">
              <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-6">
                {fw.kicker || "· Une vision ·"}
              </div>
              <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#0A0A0A] mb-10">
                {fw.title || "Mot du Fondateur"}
              </h2>

              <div className="max-w-2xl">
                {paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={`text-base md:text-lg leading-[1.9] text-[#0A0A0A]/75 font-light ${i < paragraphs.length - 1 ? "mb-6" : ""}`}
                  >
                    {p}
                  </p>
                ))}
              </div>

              {/* Signature */}
              <div className="mt-12 md:mt-14 pt-8 border-t border-[#0A0A0A]/10 max-w-2xl">
                <div className="font-serif italic text-2xl md:text-[1.7rem] text-[#0A0A0A] mb-2">
                  {fw.signature_name || "Le Fondateur"}
                </div>
                <div className="text-[0.62rem] md:text-[0.7rem] tracking-[0.4em] uppercase text-[#0A0A0A]/55">
                  {fw.signature_role || "Boulay Beach Resort"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
        );
      })()}

      {/* ─── OFFRES DE LA SEMAINE (iter-87 · Dashboard-driven) ─── */}
      {weekOffers.length > 0 && (
        <section
          className="py-24 md:py-32 bg-[#FAF7F2]"
          data-testid="vitrine-week-offers"
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14 md:mb-20">
              <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-5">
                · Réservez ·
              </div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.15] text-[#0A0A0A]">
                Les offres de la semaine.
              </h2>
              <div className="w-12 h-px bg-[#B8922A] mx-auto mt-8" />
              <p className="mt-8 max-w-xl mx-auto text-[#0A0A0A]/65 text-sm md:text-base font-light">
                Toutes les expériences disponibles à la réservation, actualisées
                en direct depuis notre agenda.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {weekOffers.map((o) => (
                <Link
                  key={o.key}
                  to={o.to}
                  onClick={() =>
                    trackEvent("week_offer_click", { key: o.key, kind: o.kind })
                  }
                  className="group relative flex flex-col overflow-hidden bg-white border border-[#0A0A0A]/8 hover:border-[#B8922A]/40 transition-colors"
                  data-testid={`week-offer-${o.kind}-${o.key.split("-").slice(1).join("-")}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
                    {o.image ? (
                      <img
                        src={o.image}
                        alt={o.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                      />
                    ) : (
                      // Elegant fallback when the event has no image_url yet —
                      // preserves the premium look next to branded Beach Life cards.
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1710] via-[#2a2419] to-[#0A0A0A]">
                        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #D4B256 0%, transparent 55%)" }} />
                        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                          <span className="font-serif font-light text-white/60 text-2xl md:text-3xl leading-tight break-words">
                            {o.title}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span
                        className={`text-[0.55rem] uppercase tracking-[0.32em] px-2 py-1 font-medium ${
                          o.kind === "event"
                            ? "bg-[#B8922A] text-white"
                            : "bg-white/90 text-[#0A0A0A]"
                        }`}
                      >
                        {o.kind === "event" ? "Événement spécial" : "Beach Life"}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="font-serif font-light text-xl md:text-2xl leading-tight text-[#0A0A0A] mb-2 group-hover:text-[#B8922A] transition-colors">
                      {o.title}
                    </h3>
                    {o.subtitle && (
                      <p className="text-[0.82rem] text-[#0A0A0A]/60 font-light leading-snug mb-3 line-clamp-2">
                        {o.subtitle}
                      </p>
                    )}
                    {o.kind === "event" && o.date && (
                      <div
                        className="inline-flex items-center gap-1.5 text-[0.72rem] text-[#B8922A] uppercase tracking-[0.14em] mb-3 capitalize"
                        data-testid={`week-offer-date-${o.key}`}
                      >
                        <Calendar size={12} strokeWidth={1.5} />
                        {fmtOfferDateFR(o.date)}
                      </div>
                    )}
                    <div className="mt-auto pt-4 flex items-end justify-between gap-3 border-t border-[#0A0A0A]/8">
                      {o.price ? (
                        <div>
                          <div className="text-[0.55rem] tracking-[0.28em] uppercase text-[#0A0A0A]/40 mb-0.5">
                            À partir de
                          </div>
                          <div
                            className="text-base md:text-lg font-medium text-[#0A0A0A]"
                            data-testid={`week-offer-price-${o.key}`}
                          >
                            {fmtOfferPriceFR(o.price)}
                          </div>
                        </div>
                      ) : (
                        <span />
                      )}
                      <span
                        className="inline-flex items-center gap-1.5 text-[0.65rem] tracking-[0.32em] uppercase text-[#0A0A0A] group-hover:text-[#B8922A] transition-colors"
                        data-testid={`week-offer-cta-${o.key}`}
                      >
                        Réserver <ArrowRight size={12} strokeWidth={1.5} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── FAQ ─────────────────────────────────────────── */}
      {(faqCfg.items || []).length > 0 && (
        <section className="py-24 md:py-32 bg-white" data-testid="vitrine-faq">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12 md:mb-16">
              <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-5">
                · FAQ ·
              </div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.15] text-[#0A0A0A]">
                {faqCfg.section_title || "Questions fréquentes"}
              </h2>
              <div className="w-12 h-px bg-[#B8922A] mx-auto mt-8" />
            </div>
            <div className="space-y-3">
              {(faqCfg.items || []).map((it, i) => (
                <details
                  key={i}
                  className="group border border-[#0A0A0A]/10 bg-white open:border-[#B8922A]/40 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 text-sm md:text-base font-medium text-[#0A0A0A] hover:text-[#B8922A]">
                    <span>{it.q}</span>
                    <span className="text-[#B8922A] text-xl font-light leading-none transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <div className="px-5 pb-5 -mt-1 text-sm text-[#0A0A0A]/75 leading-relaxed">
                    {it.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── JOURNAL — recent blog articles ───────────────── */}
      {blogArticles.length > 0 && (
        <section className="py-24 md:py-32 bg-[#FAF7F2]" data-testid="vitrine-blog-preview">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-5">
                · Notre Blog ·
              </div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.15] text-[#0A0A0A]">
                Récits de l'île.
              </h2>
              <div className="w-12 h-px bg-[#B8922A] mx-auto mt-8" />
            </div>
            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              {blogArticles.slice(0, 3).map((a) => (
                <Link
                  key={a.id}
                  to={`/blog/${a.slug}`}
                  className="group block"
                  data-testid={`landing-blog-${a.slug}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-white mb-6">
                    {a.cover_image_url && (
                      <img
                        src={a.cover_image_url}
                        alt={a.title}
                        className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.05]"
                      />
                    )}
                  </div>
                  <div className="text-[0.55rem] tracking-[0.45em] uppercase text-[#B8922A] mb-3">
                    {a.category || "Journal"}
                  </div>
                  <h3 className="font-serif font-light text-xl md:text-2xl leading-tight text-[#0A0A0A] mb-3 group-hover:text-[#B8922A] transition-colors">
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="text-sm text-[#0A0A0A]/65 leading-relaxed line-clamp-2 font-light">
                      {a.excerpt}
                    </p>
                  )}
                </Link>
              ))}
            </div>
            <div className="text-center mt-14">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.35em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/60 pb-1 hover:text-[#B8922A] hover:border-[#B8922A] transition-colors"
                data-testid="landing-blog-all"
              >
                Tous les articles
                <ArrowRight size={11} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ─── (Newsletter footer retirée — remplacée par la pop-up première visite, iter-51) ─── */}

      {/* ─── FINAL — RÉSERVATION (CTA minimaliste premium) ────────────────────────── */}
      <section
        className="relative py-40 md:py-56 overflow-hidden"
        data-testid="final-cta-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/trz2j0jd_BEACH%20CLUB.png)",
          }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-16">
            Votre prochaine évasion<br />commence ici.
          </h2>
          <a
            href="#experiences"
            onClick={(e) => {
              e.preventDefault();
              trackEvent("start_booking", { source: "landing_final" });
              const el = document.getElementById("experiences");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-block text-[0.7rem] tracking-[0.4em] uppercase text-white border-b border-white pb-2 hover:text-[#D4B256] hover:border-[#D4B256] transition-colors cursor-pointer"
            data-testid="final-cta-reserver"
          >
            Réserver maintenant
          </a>
        </div>
      </section>
    </div>
  );
}

/**
 * Vertical rectangle universe card — 2 per row.
 * Image above (aspect 4/5), title + description below.
 * Two CTAs : "Découvrir" (link to universe page) and "Réserver" (direct
 * link to the matching booking tunnel /booking/<offerId>).
 */
function UniversCard({ u }) {
  return (
    <article className="group block" data-testid={`univers-card-${u.to.split("/").pop()}`}>
      <Link
        to={u.to}
        onClick={() => trackEvent("view_offer", { offer: u.name })}
        className="block"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#FAF7F2]">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.8s] ease-out group-hover:scale-[1.04]"
            style={{ backgroundImage: `url(${u.image})` }}
          />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      </Link>
      <div className="pt-7 px-1">
        <h3 className="font-serif font-light text-2xl md:text-3xl leading-tight text-[#0A0A0A] mb-4">
          {u.name}
        </h3>
        <p className="text-sm md:text-base text-[#0A0A0A]/65 leading-relaxed line-clamp-3 font-light mb-6">
          {u.description}
        </p>
        <div className="flex items-center gap-6">
          <Link
            to={u.to}
            className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.35em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A]/60 pb-1 hover:text-[#B8922A] hover:border-[#B8922A] transition-colors"
            data-testid={`univers-discover-${u.to.split("/").pop()}`}
          >
            Découvrir
            <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </article>
  );
}

function ExperiencesCarousel({ experiences }) {
  const scrollerRef = useRef(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [openExperience, setOpenExperience] = useState(null); // { name, options[] }

  function onMouseDown(e) {
    const el = scrollerRef.current;
    if (!el) return;
    isDown.current = true;
    setDragging(true);
    startX.current = e.pageX - el.offsetLeft;
    startScroll.current = el.scrollLeft;
  }
  function onMouseLeave() { isDown.current = false; setDragging(false); }
  function onMouseUp() { isDown.current = false; setDragging(false); }
  function onMouseMove(e) {
    if (!isDown.current) return;
    e.preventDefault();
    const el = scrollerRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = startScroll.current - (x - startX.current) * 1.2;
  }

  return (
    <>
      <div
        ref={scrollerRef}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        className={`flex gap-4 sm:gap-5 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 select-none scrollbar-hide ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", scrollPaddingLeft: "1.5rem" }}
        data-testid="experiences-carousel"
      >
        <div className="flex-shrink-0 w-6 sm:w-8 md:w-10 lg:w-12" aria-hidden="true" />
        {experiences.map((e) => (
          <ExperienceCard
            key={e.key}
            e={e}
            dragging={dragging}
            onOpen={() => setOpenExperience(e)}
          />
        ))}
        <div className="flex-shrink-0 w-6 sm:w-8 md:w-10 lg:w-12" aria-hidden="true" />
      </div>

      <ExperiencePopup
        experience={openExperience}
        onClose={() => setOpenExperience(null)}
      />
    </>
  );
}

function ExperienceCard({ e, dragging, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => {
        if (dragging) return;
        trackEvent("view_experience", { experience: e.key });
        onOpen?.();
      }}
      className="group relative block flex-shrink-0 w-[78vw] sm:w-[62vw] md:w-[46vw] lg:w-[30vw] xl:w-[28vw] max-w-[560px] aspect-[3/4.2] overflow-hidden bg-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#B8922A] snap-start text-left"
      data-testid={`experience-card-${e.key}`}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.8s] ease-out group-hover:scale-[1.06] pointer-events-none"
        style={{ backgroundImage: `url(${e.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/60 pointer-events-none" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 md:px-10 pointer-events-none">
        <h3 className="font-serif font-light text-white text-3xl md:text-4xl xl:text-[2.6rem] leading-[1.05] mb-5 md:mb-6">
          {e.name}
        </h3>
        <p className="text-sm md:text-base text-white/85 leading-relaxed font-light max-w-xs md:max-w-sm mb-8 md:mb-10">
          {e.description}
        </p>
        <span
          className="relative inline-block text-[0.7rem] md:text-[0.75rem] tracking-[0.4em] uppercase text-white pb-1.5 after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:h-px after:w-6 after:bg-white/70 after:transition-all after:duration-500 group-hover:after:w-full group-hover:after:bg-[#D4B256]"
          data-testid={`experience-discover-${e.key}`}
        >
          Découvrir
        </span>
      </div>
    </button>
  );
}

function ExperiencePopup({ experience, onClose }) {
  // Close on ESC
  useEffect(() => {
    if (!experience) return;
    const onKey = (ev) => { if (ev.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [experience, onClose]);

  if (!experience) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-6 animate-fade-in"
      onClick={onClose}
      data-testid="experience-popup"
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/55 backdrop-blur-md" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.55)] animate-modal-in"
        onClick={(ev) => ev.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Expériences ${experience.name}`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 p-1.5 text-[#0A0A0A]/50 hover:text-[#0A0A0A] transition-colors"
          data-testid="experience-popup-close"
        >
          <X size={20} strokeWidth={1.5} />
        </button>

        <div className="p-8 sm:p-10 text-center">
          <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-4">
            · Life is Here ·
          </div>
          <h3 className="font-serif font-light text-3xl sm:text-4xl leading-[1.05] text-[#0A0A0A] mb-3">
            {experience.name}
          </h3>
          <p className="text-sm text-[#0A0A0A]/60 leading-relaxed max-w-xs mx-auto mb-8">
            Choisissez l&apos;expérience qui vous attend.
          </p>

          <div className="flex flex-col gap-3" data-testid="experience-popup-options">
            {experience.options.map((opt, i) => (
              <Link
                key={opt.to}
                to={opt.to}
                onClick={() => {
                  trackEvent("experience_option_select", { experience: experience.key, option: opt.label });
                  onClose();
                }}
                style={{ animationDelay: `${80 + i * 60}ms` }}
                className="group inline-flex items-center justify-between gap-4 border border-[#0A0A0A]/15 hover:border-[#0A0A0A] px-6 py-4 text-left transition-colors animate-fade-in-up"
                data-testid={`experience-popup-option-${i}`}
              >
                <span className="font-serif font-light text-lg text-[#0A0A0A]">{opt.label}</span>
                <ArrowRight size={16} strokeWidth={1.5} className="text-[#0A0A0A]/40 group-hover:text-[#B8922A] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── Newsletter section ────────────────────────────────────── */
function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  async function onSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND}/api/newsletter-subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim() || null,
          source: "landing_newsletter",
        }),
      });
      if (!res.ok) throw new Error("network");
      trackEvent("submit_lead", { channel: "newsletter", source: "landing" });
      setDone(true);
    } catch {
      setError("Impossible d'enregistrer votre email. Réessayez dans un instant.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="relative py-24 md:py-32 bg-[#FAF7F2]" data-testid="landing-newsletter">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#B8922A] mb-5">
          · Newsletter ·
        </div>
        <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] text-[#0A0A0A] mb-8">
          Restez en lien avec l'île.
        </h2>
        <p className="text-base md:text-lg text-[#0A0A0A]/65 leading-relaxed font-light mb-12 max-w-xl mx-auto">
          Nos prochaines soirées, les nouveautés du Kaai, les offres réservées
          aux abonnés. Une fois par mois, rien de plus.
        </p>

        {done ? (
          <div
            className="inline-flex items-center gap-3 border border-[#B8922A] px-8 py-5 text-[#B8922A]"
            data-testid="newsletter-success"
          >
            <Check size={18} strokeWidth={1.5} />
            <span className="text-sm tracking-[0.2em] uppercase">
              Merci, vous êtes inscrit.
            </span>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 max-w-lg mx-auto" data-testid="newsletter-form">
            <input
              type="text"
              placeholder="Prénom (facultatif)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full bg-transparent border-b border-[#0A0A0A]/25 py-4 px-1 text-[#0A0A0A] placeholder-[#0A0A0A]/40 focus:outline-none focus:border-[#B8922A] transition-colors text-center"
              data-testid="newsletter-firstname"
              maxLength={80}
            />
            <input
              type="email"
              required
              placeholder="Adresse email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-[#0A0A0A]/25 py-4 px-1 text-[#0A0A0A] placeholder-[#0A0A0A]/40 focus:outline-none focus:border-[#B8922A] transition-colors text-center"
              data-testid="newsletter-email"
            />
            {error && (
              <p className="text-sm text-[#C24226] pt-2" data-testid="newsletter-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-[#0A0A0A] border-b border-[#0A0A0A] pb-2 hover:text-[#B8922A] hover:border-[#B8922A] transition-colors disabled:opacity-50"
              data-testid="newsletter-submit"
            >
              {submitting ? "Envoi en cours…" : "M'inscrire"}
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
